import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  type ParticipantsResponse,
  ParticipantsResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/participants'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

// Legacy-shaped (flag-off) types for the `participants` page-data endpoint. The generated
// `ParticipantsResponse` is the canonical shape consumers use; these types and the legacy →
// proto shim below exist only while the `participants_proto` backend flag is rolling out and
// should be deleted once it is fully enabled.
export interface LegacyParticipantsData {
  participants: LegacyParticipant[]
  totalCount: number
}

export interface LegacyParticipant {
  id: number
  displayLogin: string
  avatarUrl: string
  profilePath: string
  slug?: string
}

const PARTICIPANTS_RESPONSE_TYPE_NAME: ParticipantsResponse['$typeName'] =
  'github.pull_requests.v1.ParticipantsResponse'

function isParticipantsResponse(
  response: ParticipantsResponse | LegacyParticipantsData,
): response is ParticipantsResponse {
  return '$typeName' in response && response.$typeName === PARTICIPANTS_RESPONSE_TYPE_NAME
}

/**
 * Temporary legacy (flag-off) JSON → canonical proto shape. The `id` becomes a `bigint`
 * (int64) and an absent `slug` becomes the empty-string proto3 default. Delete with the flag.
 */
export function participantsToProto(data: LegacyParticipantsData): ParticipantsResponse {
  return create(ParticipantsResponseSchema, {
    participants: (data.participants ?? []).map(participant => ({
      id: BigInt(participant.id),
      displayLogin: participant.displayLogin,
      avatarUrl: participant.avatarUrl,
      profilePath: participant.profilePath,
      slug: participant.slug ?? '',
    })),
    totalCount: data.totalCount,
  })
}

/**
 * Fetches the participants page-data endpoint, decoding the proto3-JSON response when the
 * backend sends it (flag on, `$typeName` present) and shimming the legacy JSON up to the same
 * proto shape otherwise (flag off). The legacy branch is temporary and removed with the flag.
 */
export async function participantsQueryFn(
  queryKey: Parameters<typeof queryFnFetch>[0],
): Promise<ParticipantsResponse | null> {
  const response = await queryFnFetch<typeof ParticipantsResponseSchema, LegacyParticipantsData | null>(queryKey, {
    schema: ParticipantsResponseSchema,
  })

  // The endpoint can resolve to `null` when there is no data to render; the consumer treats
  // that as "no participants" (renders nothing), matching the pre-proto behavior.
  if (response == null) {
    return null
  }

  return isParticipantsResponse(response) ? response : participantsToProto(response)
}
